// frontend-react/src/pages/PermissionManagementPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  message,
  Select,
  Row,
  Col,
  Statistic,
  Badge,
  Descriptions,
  Modal,
  Form,
  Input,
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  FolderOpenOutlined,
  SettingOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import PageLayout from '../components/PageLayout';
import { getApiBaseUrl } from '../config/api';
import './PermissionManagementPage.css';

const { Option } = Select;

// Types for multi-company permission system
interface User {
  id: number;
  name: string;
  email: string;
  position: string;
  phone: string;
  company: {
    id: number;
    name: string;
    code: string;
  };
  department: {
    id: number;
    name: string;
  };
  role: {
    code: string;
    name: string;
  };
  permissions: {
    can_view_all_company_docs: boolean;
    can_view_department_docs: boolean;
    can_assign_legal: boolean;
    can_approve_reject: boolean;
    can_submit_docs: boolean;
  };
  document_stats: {
    total: number;
    pending: number;
    in_progress: number;
    completed: number;
    rejected: number;
  };
}

interface Document {
  id: number;
  case_no: string;
  title: string;
  status: string;
  priority: string;
  company_id: number;
  department_id: number;
  business_owner_id: number;
  deadline: string;
  created_at: string;
}

interface Company {
  id: number;
  name: string;
  code: string;
  domain: string;
  departments: Array<{
    id: number;
    name: string;
    manager_id: number;
  }>;
  department_count: number;
}

interface SimpleUserOption {
  id: number;
  email: string;
  name?: string;
}

// const statusMap = {
//     PENDING: { color: 'orange', text: '待審閱' },
//     IN_PROGRESS: { color: 'blue', text: '進行中' },
//     COMPLETED: { color: 'green', text: '已完成' },
//     REJECTED: { color: 'red', text: '已退回' },
// }; // Not used currently

const PermissionManagementPage: React.FC = () => {
  // const [users, setUsers] = useState<User[]>([]); // Not used currently
  const [documents, setDocuments] = useState<Document[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<SimpleUserOption[]>([]);

  // filters
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filterPriority, setFilterPriority] = useState<string | undefined>(undefined);

  // 檢查是否有權限查看公司管理功能
  const canViewCompanyManagement = (): boolean => {
    if (!currentUser) return false;

    // 允許 admin@legalcontract.com 查看
    if (currentUser.email === 'admin@legalcontract.com') return true;

    // 允許律果科技的用戶查看
    if (currentUser.company?.code === 'LEGALTECH' || currentUser.company?.code === 'LGLAB')
      return true;

    // 允許 ADMIN 角色查看
    if (currentUser.role?.code === 'ADMIN' || currentUser.role?.code === 'SUPER_ADMIN') return true;

    return false;
  };

  // create document modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm] = Form.useForm();

  // assign legal modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningDocId, setAssigningDocId] = useState<number | null>(null);
  const [selectedLegalUserId, setSelectedLegalUserId] = useState<number | null>(null);

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // 獲取公司列表
      const apiBaseUrl = getApiBaseUrl();
      const token = localStorage.getItem('auth_token');
      const companiesResponse = await fetch(`${apiBaseUrl}/api/v1/company/permissions/companies`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (companiesResponse.ok) {
        const contentType = companiesResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const companiesData = await companiesResponse.json();
          setCompanies(companiesData.data.companies);
        } else {
          console.error('Companies API returned non-JSON response');
        }
      }
      // 載入用戶清單（供切換測試）
      try {
        const usersResp = await fetch(`${apiBaseUrl}/api/v1/user-init/list-all-users`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (usersResp.ok) {
          const usersData = await usersResp.json();
          const list: SimpleUserOption[] = (usersData.all_users || []).map((u: any) => ({
            id: u.id,
            email: u.email,
            name: u.name,
          }));
          setUsers(list);
        }
      } catch (e) {
        // optional
      }

      // 預設載入當前登入用戶（me）資訊與文件
      await fetchMeDetails();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('獲取數據失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserDetails = async (userId: number) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiBaseUrl}/api/v1/company/permissions/user/${userId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setCurrentUser(data.data);

          // 獲取用戶文件
          const qs: string[] = [];
          if (filterStatus) qs.push(`status=${encodeURIComponent(filterStatus)}`);
          if (filterPriority) qs.push(`priority=${encodeURIComponent(filterPriority)}`);
          const query = qs.length ? `?${qs.join('&')}` : '';
          const docsResponse = await fetch(
            `${apiBaseUrl}/api/v1/company/permissions/user/${userId}/documents${query}`,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }
          );
          if (docsResponse.ok) {
            const docsContentType = docsResponse.headers.get('content-type');
            if (docsContentType && docsContentType.includes('application/json')) {
              const docsData = await docsResponse.json();
              setDocuments(docsData.data.documents);
            } else {
              console.error('Documents API returned non-JSON response');
            }
          }
        } else {
          console.error('User API returned non-JSON response');
        }
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      message.error('獲取用戶詳情失敗');
    }
  };

  // 以當前登入者身份載入權限與文件
  const fetchMeDetails = async () => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const meResp = await fetch(`${apiBaseUrl}/api/v1/company/permissions/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meResp.ok) {
        const meData = await meResp.json();
        setCurrentUser(meData.data);
      }

      const qs: string[] = [];
      if (filterStatus) qs.push(`status=${encodeURIComponent(filterStatus)}`);
      if (filterPriority) qs.push(`priority=${encodeURIComponent(filterPriority)}`);
      const query = qs.length ? `?${qs.join('&')}` : '';
      const docsResp = await fetch(
        `${apiBaseUrl}/api/v1/company/permissions/me/documents${query}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (docsResp.ok) {
        const docsData = await docsResp.json();
        setDocuments(docsData.data.documents);
      }
    } catch (error) {
      console.error('Failed to fetch me details:', error);
    }
  };

  const onChangeStatus = async (value?: string) => {
    setFilterStatus(value);
    // reload according to context: me or selected user
    if (selectedUserId) {
      await fetchUserDetails(selectedUserId);
    } else {
      await fetchMeDetails();
    }
  };

  const onChangePriority = async (value?: string) => {
    setFilterPriority(value);
    if (selectedUserId) {
      await fetchUserDetails(selectedUserId);
    } else {
      await fetchMeDetails();
    }
  };

  // 建立文件提交
  const handleCreateSubmit = async (values: any) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${apiBaseUrl}/api/v1/company/permissions/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(values),
      });
      if (resp.ok) {
        message.success('建立文件成功');
        setShowCreateModal(false);
        createForm.resetFields();
        if (selectedUserId) {
          await fetchUserDetails(selectedUserId);
        } else {
          await fetchMeDetails();
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        message.error(`建立失敗：${err.detail || resp.status}`);
      }
    } catch (e: any) {
      message.error(`建立失敗：${e.message}`);
    }
  };

  // 指派提交
  const handleAssignSubmit = async () => {
    if (!assigningDocId || !selectedLegalUserId) return;
    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(
        `${apiBaseUrl}/api/v1/company/permissions/documents/${assigningDocId}/assign`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ legal_user_id: selectedLegalUserId }),
        }
      );
      if (resp.ok) {
        message.success('指派成功');
        setShowAssignModal(false);
        setAssigningDocId(null);
        setSelectedLegalUserId(null);
        if (selectedUserId) {
          await fetchUserDetails(selectedUserId);
        } else {
          await fetchMeDetails();
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        message.error(`指派失敗：${err.detail || resp.status}`);
      }
    } catch (e: any) {
      message.error(`指派失敗：${e.message}`);
    }
  };

  // 法務核准/退回
  const handleApprove = async (documentId: number) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(
        `${apiBaseUrl}/api/v1/company/permissions/documents/${documentId}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({}),
        }
      );
      if (resp.ok) {
        message.success('已核准');
        if (selectedUserId) {
          await fetchUserDetails(selectedUserId);
        } else {
          await fetchMeDetails();
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        message.error(`核准失敗：${err.detail || resp.status}`);
      }
    } catch (e: any) {
      message.error(`核准失敗：${e.message}`);
    }
  };

  const handleReject = async (documentId: number) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(
        `${apiBaseUrl}/api/v1/company/permissions/documents/${documentId}/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({}),
        }
      );
      if (resp.ok) {
        message.success('已退回');
        if (selectedUserId) {
          await fetchUserDetails(selectedUserId);
        } else {
          await fetchMeDetails();
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        message.error(`退回失敗：${err.detail || resp.status}`);
      }
    } catch (e: any) {
      message.error(`退回失敗：${e.message}`);
    }
  };

  const handleUserSelect = (userId: number) => {
    setSelectedUserId(userId);
    fetchUserDetails(userId);
  };

  const getRoleTag = (role: string) => {
    const roleConfig = {
      ADMIN: { color: 'red', icon: <SettingOutlined />, text: '系統管理員' },
      MANAGER: { color: 'blue', icon: <TeamOutlined />, text: '部門主管' },
      LEGAL: { color: 'green', icon: <UserOutlined />, text: '法務人員' },
      BUSINESS: { color: 'orange', icon: <FolderOpenOutlined />, text: '業務人員' },
    };

    const config = roleConfig[role as keyof typeof roleConfig] || {
      color: 'default',
      icon: null,
      text: role,
    };

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { status: 'warning', text: '待審閱' },
      IN_PROGRESS: { status: 'processing', text: '進行中' },
      COMPLETED: { status: 'success', text: '已完成' },
      REJECTED: { status: 'error', text: '已退回' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      status: 'default',
      text: status,
    };

    return <Badge status={config.status as any} text={config.text} />;
  };

  const getPriorityTag = (priority: string) => {
    const priorityConfig = {
      HIGH: { color: 'red', text: '高' },
      NORMAL: { color: 'blue', text: '中' },
      LOW: { color: 'green', text: '低' },
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || {
      color: 'default',
      text: priority,
    };

    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Commented out as not used currently
  // const handleApprove = (record: any) => {
  //     message.success(`案件 ${record.case_no} 已核准`);
  //     // Call API to approve
  // };

  // const handleReject = (record: any) => {
  //     message.warning(`案件 ${record.case_no} 已退回`);
  //     // Call API to reject
  // };

  const companyColumns = [
    {
      title: '公司名稱',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Company) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.code}</div>
        </div>
      ),
    },
    {
      title: '網域',
      dataIndex: 'domain',
      key: 'domain',
    },
    {
      title: '部門數',
      dataIndex: 'department_count',
      key: 'department_count',
      render: (count: number) => <Tag color='blue'>{count} 個部門</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      render: () => (
        <Button type='link' icon={<EyeOutlined />} onClick={() => setShowUserModal(true)}>
          查看詳情
        </Button>
      ),
    },
  ];

  const documentColumns = [
    {
      title: '案件編號',
      dataIndex: 'case_no',
      key: 'case_no',
    },
    {
      title: '文件名稱',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusBadge(status),
    },
    {
      title: '優先級',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => getPriorityTag(priority),
    },
    {
      title: '期限',
      dataIndex: 'deadline',
      key: 'deadline',
      render: (deadline: string) => (deadline ? new Date(deadline).toLocaleDateString('zh-TW', { timeZone: 'Asia/Taipei' }) : '-'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Document) => (
        <div>
          <Button type='link' icon={<EyeOutlined />} size='small'>
            查看
          </Button>
          {/* Admin/Manager can assign */}
          {currentUser?.permissions?.can_assign_legal && (
            <Button
              type='link'
              size='small'
              onClick={() => {
                setAssigningDocId(record.id);
                setShowAssignModal(true);
              }}
            >
              指派法務
            </Button>
          )}
          {/* Legal can approve/reject */}
          {currentUser?.permissions?.can_approve_reject && (
            <>
              <Button type='link' size='small' onClick={() => handleApprove(record.id)}>
                核准
              </Button>
              <Button type='link' size='small' onClick={() => handleReject(record.id)}>
                退回
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageLayout>
      <div className='page-content'>
        <div className='page-header'>
          <h1>權限與文件管理</h1>
          <p>管理多公司權限體系與文件訪問控制</p>
        </div>

        {/* 統計卡片 */}
        {currentUser && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title='待審閱'
                  value={currentUser.document_stats.pending}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title='進行中'
                  value={currentUser.document_stats.in_progress}
                  valueStyle={{ color: '#1E88A8' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title='已完成'
                  value={currentUser.document_stats.completed}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title='總計'
                  value={currentUser.document_stats.total}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 用戶信息 */}
        {currentUser && (
          <Card title='當前用戶信息' style={{ marginBottom: 24 }}>
            <Descriptions column={2}>
              <Descriptions.Item label='姓名'>{currentUser.name}</Descriptions.Item>
              <Descriptions.Item label='郵箱'>{currentUser.email}</Descriptions.Item>
              <Descriptions.Item label='職位'>{currentUser.position}</Descriptions.Item>
              <Descriptions.Item label='電話'>{currentUser.phone}</Descriptions.Item>
              <Descriptions.Item label='公司'>{currentUser.company?.name}</Descriptions.Item>
              <Descriptions.Item label='部門'>{currentUser.department?.name}</Descriptions.Item>
              <Descriptions.Item label='角色'>
                {getRoleTag(currentUser.role.code)}
              </Descriptions.Item>
              <Descriptions.Item label='權限'>
                <div>
                  {currentUser.permissions.can_view_all_company_docs && (
                    <Tag color='red'>全公司文件</Tag>
                  )}
                  {currentUser.permissions.can_view_department_docs && (
                    <Tag color='blue'>部門文件</Tag>
                  )}
                  {currentUser.permissions.can_assign_legal && <Tag color='green'>指派法務</Tag>}
                  {currentUser.permissions.can_approve_reject && <Tag color='orange'>核准退回</Tag>}
                  {currentUser.permissions.can_submit_docs && <Tag color='purple'>提交文件</Tag>}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* 公司列表 - 僅限系統管理員和律果科技用戶 */}
        {canViewCompanyManagement() && (
          <Card
            title='公司管理'
            style={{ marginBottom: 24 }}
            extra={
              <Button type='primary' onClick={() => setShowUserModal(true)}>
                查看用戶列表
              </Button>
            }
          >
            <Table
              columns={companyColumns}
              dataSource={companies}
              rowKey='id'
              loading={loading}
              pagination={false}
            />
          </Card>
        )}

        {/* 文件列表 */}
        {documents.length > 0 && (
          <Card
            title={`我的文件清單 (共 ${documents.length} 份文件)`}
            extra={
              <div>
                {/* Create document (Business) */}
                <Button
                  type='primary'
                  style={{ marginRight: 12 }}
                  onClick={() => setShowCreateModal(true)}
                >
                  新建文件
                </Button>
                <span style={{ marginRight: 8 }}>狀態：</span>
                <Select
                  allowClear
                  placeholder='全部'
                  value={filterStatus}
                  onChange={onChangeStatus}
                  style={{ width: 140, marginRight: 12 }}
                >
                  <Option value='PENDING'>待審閱</Option>
                  <Option value='IN_PROGRESS'>進行中</Option>
                  <Option value='COMPLETED'>已完成</Option>
                  <Option value='REJECTED'>已退回</Option>
                </Select>
                <span style={{ marginRight: 8 }}>優先級：</span>
                <Select
                  allowClear
                  placeholder='全部'
                  value={filterPriority}
                  onChange={onChangePriority}
                  style={{ width: 120, marginRight: 12 }}
                >
                  <Option value='HIGH'>高</Option>
                  <Option value='NORMAL'>中</Option>
                  <Option value='LOW'>低</Option>
                </Select>
                <Button
                  onClick={() =>
                    selectedUserId ? fetchUserDetails(selectedUserId) : fetchMeDetails()
                  }
                >
                  重新整理
                </Button>
              </div>
            }
          >
            <Table
              columns={documentColumns}
              dataSource={documents}
              rowKey='id'
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 條，共 ${total} 條記錄`,
              }}
            />
          </Card>
        )}

        {/* 測試用戶選擇 */}
        <Card title='測試功能' style={{ marginTop: 24 }}>
          <Form layout='inline'>
            <Form.Item label='選擇測試用戶'>
              <Select
                showSearch
                placeholder='選擇用戶查看權限'
                style={{ width: 320 }}
                onChange={handleUserSelect}
                optionFilterProp='label'
                value={selectedUserId}
              >
                {users.map(u => (
                  <Option
                    key={u.id}
                    value={u.id}
                    label={`${u.email} ${u.name ? `(${u.name})` : ''}`}
                  >
                    {u.email} {u.name ? `(${u.name})` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button
                type='primary'
                onClick={() =>
                  selectedUserId ? fetchUserDetails(selectedUserId) : fetchMeDetails()
                }
              >
                載入用戶資料
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                onClick={() => {
                  setSelectedUserId(null);
                  fetchMeDetails();
                }}
              >
                切回當前登入者
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>

      {/* 用戶詳情 Modal */}
      <Modal
        title='用戶列表'
        open={showUserModal}
        onCancel={() => setShowUserModal(false)}
        footer={null}
        width={800}
      >
        <p>此功能將顯示所有用戶的詳細列表，包括角色分配和權限設定。</p>
      </Modal>
      {/* 新建文件 Modal */}
      <Modal
        title='新建文件'
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onOk={() => createForm.submit()}
        okText='建立'
      >
        <Form
          form={createForm}
          layout='vertical'
          onFinish={handleCreateSubmit}
          initialValues={{ priority: 'NORMAL', deadline_days: 7 }}
        >
          <Form.Item
            name='title'
            label='文件名稱'
            rules={[{ required: true, message: '請輸入文件名稱' }]}
          >
            <Input placeholder='例如：合作協議草案' />
          </Form.Item>
          <Form.Item name='priority' label='優先級' rules={[{ required: true }]}>
            <Select>
              <Option value='HIGH'>高</Option>
              <Option value='NORMAL'>中</Option>
              <Option value='LOW'>低</Option>
            </Select>
          </Form.Item>
          <Form.Item name='deadline_days' label='期限（天）' rules={[{ required: true }]}>
            <Input type='number' min={1} max={60} />
          </Form.Item>
          <Form.Item name='content' label='內容'>
            <Input.TextArea rows={4} placeholder='可選，補充詳細內容' />
          </Form.Item>
        </Form>
      </Modal>

      {/* 指派法務 Modal */}
      <Modal
        title='指派法務'
        open={showAssignModal}
        onCancel={() => {
          setShowAssignModal(false);
          setAssigningDocId(null);
          setSelectedLegalUserId(null);
        }}
        onOk={handleAssignSubmit}
        okButtonProps={{ disabled: !selectedLegalUserId }}
        okText='指派'
      >
        <p>選擇要指派的法務人員：</p>
        <Select
          showSearch
          placeholder='選擇法務人員'
          optionFilterProp='label'
          style={{ width: '100%' }}
          value={selectedLegalUserId}
          onChange={v => setSelectedLegalUserId(v ?? null)}
        >
          {users.map(u => (
            <Option key={u.id} value={u.id} label={`${u.email} ${u.name ? `(${u.name})` : ''}`}>
              {u.email} {u.name ? `(${u.name})` : ''}
            </Option>
          ))}
        </Select>
      </Modal>
    </PageLayout>
  );
};

export default PermissionManagementPage;
